<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed inset-0 z-[2600]" @close="close">
      <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-150" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-slate-950/70 backdrop-blur-sm" />
      </TransitionChild>
      <div class="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div class="flex min-h-full items-center justify-center">
          <TransitionChild as="template" enter="ease-out duration-200" enter-from="opacity-0 translate-y-3 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-3 sm:scale-95">
            <DialogPanel class="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
              <div class="border-b border-slate-200 px-6 py-5">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <p class="text-xs font-bold uppercase tracking-[0.14em] text-blue-600">Platforma SMS Text Message</p>
                    <DialogTitle class="mt-1 text-xl font-bold text-slate-900">Confirm message charge</DialogTitle>
                  </div>
                  <button type="button" class="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700" :disabled="working" @click="close">
                    <XMarkIcon class="h-5 w-5" /><span class="sr-only">Close</span>
                  </button>
                </div>
              </div>

              <div class="space-y-5 p-6">
                <p class="text-base leading-7 text-slate-700">
                  {{ confirmationLead }} <strong class="text-slate-950">{{ recipientCount }} {{ recipientCount === 1 ? 'member' : 'members' }}</strong> for a total of <strong class="text-slate-950">{{ totalPrice }}</strong> charged to the card on file.
                  <span class="mt-2 block font-semibold text-slate-900">Would you like us to {{ actionLower }} this now?</span>
                </p>

                <div v-if="hasPaymentMethod" class="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <div><p class="text-xs font-bold uppercase tracking-wide text-emerald-700">Card on file</p><p class="mt-1 text-sm font-semibold capitalize text-slate-800">{{ profile?.brand || 'Card' }} ending in {{ profile?.lastFour || '••••' }}</p></div>
                  <CheckCircleIcon class="h-7 w-7 text-emerald-500" />
                </div>

                <div v-else class="rounded-2xl border border-blue-100 bg-blue-50/50 p-4">
                  <p class="font-bold text-slate-900">Add a card to continue</p>
                  <p class="mt-1 text-xs leading-5 text-slate-500">Your card is securely saved with Stripe, then charged only after this confirmation.</p>
                  <div class="mt-4 space-y-4">
                    <label class="block"><span class="text-xs font-bold text-slate-700">Name on card</span><input v-model.trim="cardholderName" type="text" autocomplete="cc-name" class="mt-1 block h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10" /></label>
                    <div><span class="text-xs font-bold text-slate-700">Card number</span><div class="mt-1 h-11 rounded-xl border border-slate-300 bg-white px-3 pt-3"><div ref="cardNumberMount" /></div></div>
                    <div class="grid grid-cols-2 gap-3">
                      <div><span class="text-xs font-bold text-slate-700">Expiration</span><div class="mt-1 h-11 rounded-xl border border-slate-300 bg-white px-3 pt-3"><div ref="cardExpiryMount" /></div></div>
                      <div><span class="text-xs font-bold text-slate-700">CVC</span><div class="mt-1 h-11 rounded-xl border border-slate-300 bg-white px-3 pt-3"><div ref="cardCvcMount" /></div></div>
                    </div>
                  </div>
                </div>

                <p v-if="displayError" class="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ displayError }}</p>
                <p class="text-xs leading-5 text-slate-400">Final delivery totals may vary only if your selected audience changes before a scheduled send.</p>
              </div>

              <div class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:justify-end">
                <button type="button" class="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-600 transition hover:bg-slate-100" :disabled="working" @click="close">Cancel</button>
                <button type="button" class="rounded-xl bg-[#2D8CFF] px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-500/20 transition hover:bg-[#247de7] disabled:cursor-not-allowed disabled:opacity-50" :disabled="working || (!hasPaymentMethod && !cardReady)" @click="confirm">
                  {{ working ? 'Processing…' : action === 'schedule' ? 'Yes, Please Schedule' : 'Yes, Please Send' }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { CheckCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  open: { type: Boolean, default: false },
  quote: { type: Object, default: null },
  action: { type: String, default: 'send' },
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' },
})
const emit = defineEmits(['close', 'confirm', 'card-saved'])
const billingApiBase = `${process.env.VUE_APP_API_PROXY_URL || ''}/api/platforma-creator-billing`
const profileOverride = ref(null)
const cardholderName = ref('')
const localError = ref('')
const cardSaving = ref(false)
const cardReady = ref(false)
const cardNumberMount = ref(null)
const cardExpiryMount = ref(null)
const cardCvcMount = ref(null)
let stripe = null
let elements = null
let cardNumber = null
let cardExpiry = null
let cardCvc = null
let setupIntentId = ''
let clientSecret = ''
let stripeScriptPromise = null

const profile = computed(() => profileOverride.value || props.quote?.paymentProfile || null)
const hasPaymentMethod = computed(() => Boolean(profile.value?.hasPaymentMethod))
const working = computed(() => props.busy || cardSaving.value)
const displayError = computed(() => localError.value || props.error)
const recipientCount = computed(() => Number(props.quote?.recipientCount || 0))
const totalPrice = computed(() => new Intl.NumberFormat('en-US', { style: 'currency', currency: props.quote?.currencyCode || 'USD' }).format(Number(props.quote?.creatorChargeCents || 0) / 100))
const actionLower = computed(() => props.action === 'schedule' ? 'schedule' : 'send')
const confirmationLead = computed(() => props.action === 'schedule'
  ? 'Schedule this message for your selected'
  : 'Send this message now to your selected')

async function requestJson(path, options = {}) {
  const response = await fetch(`${billingApiBase}${path}`, { credentials: 'include', ...options })
  const payload = await response.json().catch(() => ({}))
  if (!response.ok || payload?.success === false) throw new Error(payload?.message || 'Billing request failed')
  return payload
}
async function loadStripe() {
  if (window.Stripe) return window.Stripe
  if (!stripeScriptPromise) stripeScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = 'https://js.stripe.com/v3/'
    script.async = true
    script.dataset.platformaStripe = 'true'
    script.onload = () => resolve(window.Stripe)
    script.onerror = () => reject(new Error('Secure card input could not be loaded'))
    document.head.appendChild(script)
  })
  return stripeScriptPromise
}
function destroyElements() {
  const mountedElements = [cardNumber, cardExpiry, cardCvc]
  mountedElements.forEach((element) => element?.destroy?.())
  cardNumber = null; cardExpiry = null; cardCvc = null; elements = null; stripe = null
  setupIntentId = ''; clientSecret = ''; cardReady.value = false
}
async function initializeCard() {
  destroyElements(); localError.value = ''
  if (hasPaymentMethod.value) return
  try {
    const intent = await requestJson('/stripe/setup-intent', { method: 'POST' })
    const Stripe = await loadStripe()
    if (!Stripe || !intent.publishableKey || !intent.clientSecret) throw new Error('Stripe billing is not configured')
    await nextTick()
    stripe = Stripe(intent.publishableKey); elements = stripe.elements()
    const style = { base: { color: '#0f172a', fontSize: '16px', '::placeholder': { color: '#94a3b8' } } }
    cardNumber = elements.create('cardNumber', { style }); cardExpiry = elements.create('cardExpiry', { style }); cardCvc = elements.create('cardCvc', { style })
    cardNumber.mount(cardNumberMount.value); cardExpiry.mount(cardExpiryMount.value); cardCvc.mount(cardCvcMount.value)
    setupIntentId = intent.setupIntentId; clientSecret = intent.clientSecret; cardReady.value = true
  } catch (error) { localError.value = error?.message || 'Secure card input could not be loaded' }
}
async function saveCard() {
  const result = await stripe.confirmCardSetup(clientSecret, { payment_method: { card: cardNumber, billing_details: { name: cardholderName.value || undefined } } })
  if (result.error) throw new Error(result.error.message || 'Please check your card details')
  const payload = await requestJson('/stripe/setup-intent/complete', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ setupIntentId: result.setupIntent?.id || setupIntentId }) })
  const savedProfile = (payload.billingProfiles || []).find((item) => item.provider === 'stripe')
  profileOverride.value = savedProfile || { hasPaymentMethod: true }
  window.dispatchEvent(new CustomEvent('platforma:creator-subscription-updated'))
  emit('card-saved', profileOverride.value)
}
async function confirm() {
  if (working.value) return
  localError.value = ''
  try {
    if (!hasPaymentMethod.value) { cardSaving.value = true; await saveCard() }
    emit('confirm')
  } catch (error) { localError.value = error?.message || 'Card setup failed' }
  finally { cardSaving.value = false }
}
function close() { if (!working.value) emit('close') }
watch(() => props.open, (isOpen) => {
  profileOverride.value = null; localError.value = ''; cardholderName.value = ''
  if (isOpen) void initializeCard(); else destroyElements()
})
onBeforeUnmount(destroyElements)
</script>
